'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

const navLinks = [
    { label: 'How It Works', href: '#how-it-works', sectionId: 'how-it-works' },
    { label: 'Use Cases', href: '#use-cases', sectionId: 'use-cases' },
    {
        label: 'Docs',
        href: 'https://github.com/Codigo-Ng/Cap-state-ext/blob/main/README.md',
        targetBlank: true,
    },
];

export default function CapStateHeader() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 40);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const sections = navLinks
            .filter((link) => link.sectionId)
            .map((link) => document.getElementById(link.sectionId!))
            .filter(Boolean);

        if (!sections.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleSections = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            b.intersectionRatio - a.intersectionRatio
                    );

                if (visibleSections.length > 0) {
                    setActiveSection(visibleSections[0].target.id);
                }
            },
            {
                rootMargin: '-25% 0px -55% 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            }
        );

        sections.forEach((section) => observer.observe(section));

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    return (
        <>
            <nav
                className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? 'py-3' : 'py-5'
                    }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div
                        className={`flex items-center justify-between rounded-2xl px-5 py-1 transition-all duration-500 ${scrolled
                            ? 'glass-panel shadow-lg shadow-black/30'
                            : ''
                            }`}
                    >
                        {/* Logo */}
                        <Link
                            href="/"
                            className="flex items-center gap-2.5 group"
                        >
                            <span className="font-bold text-lg tracking-tight text-foreground">
                                Cap
                                <span className="text-gradient-primary">
                                    State
                                </span>
                            </span>
                        </Link>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-1 glass-nav rounded-full px-2 py-1.5">
                            {navLinks.map((link) =>
                                link.targetBlank ? (
                                    <a
                                        key={link.href}
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-5 py-2 rounded-full text-xs font-700 uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-200"
                                    >
                                        {link.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        className={`px-5 py-2 rounded-full text-xs font-700 uppercase tracking-widest transition-all duration-200 ${activeSection === link.sectionId
                                            ? 'bg-primary text-black'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                )
                            )}
                        </div>

                        {/* CTA */}
                        <div className="hidden md:flex items-center py-1 gap-3">
                            <a
                                href="https://marketplace.visualstudio.com/items?itemName=CodigoFoundry.capstate-latest&ssr=false#review-details"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setMobileOpen(false)}
                                className="btn-primary w-full h-10 text-center justify-center"
                            >
                                Get Started
                            </a>
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden p-2.5 glass-nav rounded-xl text-foreground"
                            aria-label="Open menu"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M3 5h14M3 10h14M3 15h14"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-[100] flex flex-col"
                    style={{
                        background: 'rgba(10,11,15,0.97)',
                        backdropFilter: 'blur(20px)',
                    }}
                >
                    <div className="flex items-center justify-between px-6 py-5">
                        <Link
                            href="/"
                            className="flex items-center gap-2.5"
                            onClick={() => setMobileOpen(false)}
                        >
                            <span className="font-bold text-lg tracking-tight text-foreground">
                                Cap
                                <span className="text-gradient-primary">
                                    State
                                </span>
                            </span>
                        </Link>

                        <button
                            onClick={() => setMobileOpen(false)}
                            className="p-2.5 glass-nav rounded-xl text-foreground"
                            aria-label="Close menu"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M5 5l10 10M15 5L5 15"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="flex flex-col gap-2 px-6 mt-8">
                        {navLinks.map((link) =>
                            link.targetBlank ? (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setMobileOpen(false)}
                                    className="text-2xl font-700 tracking-tight text-foreground py-4 border-b border-border hover:text-primary transition-colors"
                                >
                                    {link.label}
                                </a>
                            ) : (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    className={`text-2xl font-700 tracking-tight py-4 border-b border-border transition-colors ${activeSection === link.sectionId
                                        ? 'text-primary'
                                        : 'text-foreground hover:text-primary'
                                        }`}
                                >
                                    {link.label}
                                </Link>
                            )
                        )}
                    </div>

                    <div className="px-6 mt-8">
                        <a
                            href="https://marketplace.visualstudio.com/items?itemName=CodigoFoundry.capstate-latest&ssr=false#review-details"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileOpen(false)}
                            className="btn-primary w-full text-center justify-center"
                        >
                            Get Started
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}